import { useEffect } from 'react'

import { useMutation, useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  VerifiedCallerIdOwnerType,
  VerifiedCallerIdStatus,
} from 'src/graphql/types/verifiedCallerIds'
import {
  GET_VERIFIED_CALLER_IDS,
  REMOVE_VERIFIED_CALLER_ID,
  REQUEST_CALLER_ID_VERIFICATION,
} from 'src/graphql/verifiedCallerIds.graphql'
import { usDigits } from 'src/lib/phone.utils'

const PENDING_POLL_INTERVAL_MS = 3_000

export type VerifiedCallerIdRow = {
  verifiedCallerIdId: string
  employeeId?: string | null
  ownerType: VerifiedCallerIdOwnerType
  phoneNumber: string
  status: VerifiedCallerIdStatus
  validationCode?: string | null
  failureReason?: string | null
  verifiedAt?: string | null
}

export interface VerifiedCallerIdTarget {
  ownerType: VerifiedCallerIdOwnerType
  /** Required for EMPLOYEE targets. */
  employeeId?: string | null
  /** The saved number that would be verified. */
  phoneNumber?: string | null
}

/**
 * Verification state for one phone number (an employee's or the company's),
 * always fetched fresh, polling while Twilio's verification call is in flight.
 */
export const useVerifiedCallerId = (
  { ownerType, employeeId, phoneNumber }: VerifiedCallerIdTarget,
  { skip = false }: { skip?: boolean } = {}
) => {
  const { data, loading, refetch, startPolling, stopPolling } = useQuery(
    GET_VERIFIED_CALLER_IDS,
    { fetchPolicy: 'network-only', skip }
  )

  const rows: VerifiedCallerIdRow[] = data?.verifiedCallerIds ?? []
  const ownerRows = rows.filter((row) =>
    ownerType === VerifiedCallerIdOwnerType.COMPANY
      ? row.ownerType === VerifiedCallerIdOwnerType.COMPANY
      : row.employeeId === employeeId
  )
  const targetDigits = usDigits(phoneNumber)
  const currentRow = ownerRows.find(
    (row) => usDigits(row.phoneNumber) === targetDigits
  )
  // A number this owner verified earlier and no longer has on their profile.
  const staleRow = currentRow
    ? undefined
    : ownerRows.find(
        (row) =>
          row.status === VerifiedCallerIdStatus.VERIFIED &&
          usDigits(row.phoneNumber) !== targetDigits
      )
  const status = currentRow?.status ?? null
  const isPending = status === VerifiedCallerIdStatus.PENDING

  const [requestVerification, { loading: requesting }] = useMutation(
    REQUEST_CALLER_ID_VERIFICATION,
    {
      onCompleted: (result) => {
        if (
          result.requestCallerIdVerification.status ===
          VerifiedCallerIdStatus.VERIFIED
        ) {
          toast('Phone number verified as caller ID', 'success')
        }
        refetch()
      },
      onError: (error) => {
        toast(`Could not start verification: ${error.message}`, 'error')
      },
    }
  )

  const [removeVerification, { loading: removing }] = useMutation(
    REMOVE_VERIFIED_CALLER_ID,
    {
      onCompleted: () => {
        toast('Caller ID removed', 'success')
        refetch()
      },
      onError: (error) => {
        toast(`Could not remove caller ID: ${error.message}`, 'error')
      },
    }
  )

  useEffect(() => {
    if (isPending && !skip) {
      startPolling(PENDING_POLL_INTERVAL_MS)
    } else {
      stopPolling()
    }

    return () => stopPolling()
  }, [isPending, skip, startPolling, stopPolling])

  const request = () =>
    requestVerification({
      variables: {
        input: {
          ownerType,
          ...(ownerType === VerifiedCallerIdOwnerType.EMPLOYEE && {
            employeeId,
          }),
        },
      },
    })

  const remove = (row: VerifiedCallerIdRow) =>
    removeVerification({ variables: { id: row.verifiedCallerIdId } })

  return {
    loading: loading && !data,
    currentRow,
    staleRow,
    status,
    isPending,
    request,
    remove,
    refetch,
    busy: requesting || removing,
  }
}
